/**
 * Rows for the Spend by AI Feature section (SF-4567): turning the saved or
 * live split into display rows, and choosing which source to show. Kept apart
 * from the component so it stays a components-only module (fast refresh).
 */
import type {
  EntryPointBreakdown as SavedBreakdown,
  EntryPointTotals,
  LiveEntryPointBreakdown,
} from '../api/evaUsageClient';

/** The id the untagged row is listed under. */
export const UNTAGGED_ID = 'untagged';

/** Display names for the AI features we know. Anything else shows its id. */
export const ENTRY_POINT_LABELS: Readonly<Record<string, string>> = Object.freeze({
  chat: 'Chat',
  dashboard_summary: 'Dashboard summary',
  survey: 'Conversational survey',
  title: 'Chat titles',
  mcp: 'Outside AI clients (MCP)',
  helpdesk_reply: 'Helpdesk replies',
  weekly_summary: 'Weekly summary email',
  demo_builder: 'Demo builder',
  demo_data_text: 'Demo fake data text',
  [UNTAGGED_ID]: 'Untagged (before the split)',
});

/** The label for an entry point id: a friendly name, else the id itself. */
export function entryPointLabel(id: string): string {
  return ENTRY_POINT_LABELS[id] ?? id;
}

/** One row of the section. */
export interface EntryPointRow {
  entryPoint: string;
  label: string;
  costUsd: string;
  /** AI calls; unknown for untagged spend, which predates the counter. */
  requests: number | null;
  /** Share of the month's spend, 0–100. Null when the month has no spend. */
  sharePct: number | null;
}

/** Whether a totals entry carries any spend or calls worth listing. */
function hasSpend(t: EntryPointTotals | undefined | null): boolean {
  if (!t) return false;
  return Number(t.costUsd) > 0 || (t.requests ?? 0) > 0;
}

/**
 * Turn either payload shape into rows: features most expensive first (ties by
 * id), untagged last and only when it holds spend. Money stays the API's
 * string; the share is worked out from it for display only.
 */
export function toEntryPointRows(
  source: { kind: 'saved'; data: SavedBreakdown } | { kind: 'live'; data: LiveEntryPointBreakdown },
): EntryPointRow[] {
  const parts: Array<{ entryPoint: string; totals: EntryPointTotals }> =
    source.kind === 'saved'
      ? [...(source.data.entryPoints ?? [])]
      : Object.entries(source.data.entryPoints ?? {}).map(([entryPoint, totals]) => ({
          entryPoint,
          totals,
        }));

  const features = parts
    .filter((p) => hasSpend(p.totals))
    .sort(
      (a, b) =>
        Number(b.totals.costUsd) - Number(a.totals.costUsd) ||
        a.entryPoint.localeCompare(b.entryPoint),
    );
  const untagged = source.data.untagged;
  const all = hasSpend(untagged)
    ? [...features, { entryPoint: UNTAGGED_ID, totals: untagged }]
    : features;

  const total = all.reduce((sum, p) => sum + (Number(p.totals.costUsd) || 0), 0);
  return all.map((p) => ({
    entryPoint: p.entryPoint,
    label: entryPointLabel(p.entryPoint),
    costUsd: p.totals.costUsd,
    requests: p.entryPoint === UNTAGGED_ID ? null : (p.totals.requests ?? null),
    sharePct: total > 0 ? ((Number(p.totals.costUsd) || 0) / total) * 100 : null,
  }));
}

/**
 * Pick the rows to show and whether they are live, following the page's
 * live-vs-saved rule. Loading is reported separately from empty so the
 * section never flashes "No AI usage" while a source is still loading.
 */
export function pickEntryPointRows(input: {
  isCurrentMonth: boolean;
  live: LiveEntryPointBreakdown | null | undefined;
  liveLoading: boolean;
  liveError: boolean;
  saved: SavedBreakdown | null | undefined;
  savedLoading: boolean;
  savedError: boolean;
}): { rows: EntryPointRow[]; isLive: boolean; loading: boolean; failed: boolean } {
  const { isCurrentMonth, live, liveLoading, liveError, saved, savedLoading, savedError } = input;

  if (isCurrentMonth && !liveError && live) {
    return {
      rows: toEntryPointRows({ kind: 'live', data: live }),
      isLive: true,
      loading: false,
      failed: false,
    };
  }
  // Current month, live still on its way: wait for it rather than showing the
  // saved figures (yesterday's) and then swapping.
  if (isCurrentMonth && liveLoading && !liveError) {
    return { rows: [], isLive: false, loading: true, failed: false };
  }
  if (saved) {
    return {
      rows: toEntryPointRows({ kind: 'saved', data: saved }),
      isLive: false,
      loading: false,
      failed: false,
    };
  }
  return { rows: [], isLive: false, loading: savedLoading, failed: savedError };
}
